/**
 * "What applies here?" — office · ACRISS · pickup date · rental days →
 * the floor of that cell, where it comes from, the chain the engine
 * weighed, and the rows it used lit up in the list (CU-869fkbjdh § 3.6).
 *
 * A dialog opened by "Test a cell" in the toolbar (the inline panel above
 * the table added noise — Curro, 2026-10-20). The verdict is the engine's
 * own (`setupApi.resolveLimits` → `POST /setup/price-limits/resolve` →
 * `POST /jobs/limits/resolve`, the same `ranked → resolve_first →
 * protections → compose` the sweep runs). Nothing is computed here: an
 * unreachable engine shows the error state with *Try again*, never an
 * estimate (criterion 19). While the engine answers the dialog shows an
 * unmistakable checking state — spinner, "Checking…", the form disabled.
 *
 * The component stays mounted under the tab so the last cell and its
 * verdict survive a close: reopening shows the previous inputs and
 * answer. The rows the engine used keep lighting up in the table after
 * the dialog closes (`onVerdict`); the tab decides when to let them go.
 * An offer-class axis is asked only when at least one rule uses it, and
 * then it offers the values in use plus "Another …" — never an "Any"
 * that would match a scoped rule in silence (criterion 18, option A): a
 * blank axis travels as `null` and a scoped rule does not apply to it.
 */
import React, { useEffect, useMemo, useState } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import { Autocomplete, Box, CircularProgress, Link, MenuItem, Stack, TextField, Typography } from '@mui/material'
import { AppBulkEditAction, AppDialog, AppDialogBody, AppDialogCancel, AppDialogFooter, AppDialogHeader, AppNote } from '@components'
import { dsColorNew } from '@palette'
import { DS_REPRICING_ROUTES } from '@constants/routes'
import { IconSeeker } from '@ui-seeker/core'
import DateField from 'Pages/DsRepricing/@components/DateField'
import { dialogBodySx } from 'Pages/DsRepricing/@components/dialogBodySx'
import { toIso } from 'Pages/DsRepricing/@services/dateRange'
import { setupScopeSx } from 'Pages/DsRepricing/Setup/@components/setupScopeSx'
import { inputSx } from 'Pages/DsRepricing/Setup/@components/formStyles'
import { setupApi, idKey, prepaidKey } from 'Pages/DsRepricing/Setup/@services'
import { fromEngine } from 'Pages/DsRepricing/Setup/@services/explain'
import { fmtDate, fmtMoney } from 'Pages/DsRepricing/Setup/@services/priceLimitsProjection'

const ANOTHER = 'another'

export default function ExplainDialog({ open, onClose, rules, matrices, catalog, matrixById, onVerdict }) {
	const { labels, offices, allSipps, axes } = catalog
	const [cell, setCell] = useState(() => ({ office: '', acriss: '', pickup: toIso(new Date()), lor: 5 }))
	// First office of the catalogue once it lands, so "Check" works on the first visit.
	useEffect(() => { if (!cell.office && offices.length) setCell(c => ({ ...c, office: offices[0].code })) }, [offices])

	// An axis is asked as soon as ONE rule uses it (criterion 18): the
	// values in use, plus "Another …" which travels as null.
	const inUse = useMemo(() => ({
		franchise: [...new Set(rules.map(r => idKey(r.scope_ps_provider_id)).filter(Boolean))],
		tariff:    [...new Set(rules.map(r => prepaidKey(r.scope_is_prepaid)).filter(Boolean))],
		insurance: [...new Set(rules.map(r => idKey(r.scope_ps_insurance_id)).filter(Boolean))],
	}), [rules])
	const [axisPick, setAxisPick] = useState({ franchise: '', tariff: '', insurance: '' })
	// Default: the first value in use; a value no longer in use falls back to it.
	const pick = {
		franchise: inUse.franchise.includes(axisPick.franchise) || axisPick.franchise === ANOTHER ? axisPick.franchise : inUse.franchise[0] || '',
		tariff:    inUse.tariff.includes(axisPick.tariff) || axisPick.tariff === ANOTHER ? axisPick.tariff : inUse.tariff[0] || '',
		insurance: inUse.insurance.includes(axisPick.insurance) || axisPick.insurance === ANOTHER ? axisPick.insurance : inUse.insurance[0] || '',
	}
	const franchiseLabel = (k) => axes.franchiseName(Number(k))
	const tariffLabel = (k) => (k === '1' ? 'Prepaid' : 'Non prepaid')
	const insuranceLabel = (k) => axes.insuranceName(Number(k))

	const [verdict, setVerdict] = useState(null)
	const [stale, setStale] = useState(false)
	const [loading, setLoading] = useState(false)
	const [error, setError] = useState(null)
	const canCheck = !!(cell.office && cell.acriss && cell.pickup && Number(cell.lor) >= 1) && !loading

	async function check() {
		if (!canCheck) return
		const asked = { franchise: inUse.franchise.length > 0, tariff: inUse.tariff.length > 0, insurance: inUse.insurance.length > 0 }
		const body = {
			office_code: cell.office, acriss: cell.acriss, pickup_date: cell.pickup, lor: Number(cell.lor),
			ps_provider_id:  asked.franchise && pick.franchise !== ANOTHER ? Number(pick.franchise) : null,
			is_prepaid:      asked.tariff && pick.tariff !== ANOTHER ? pick.tariff === '1' : null,
			ps_insurance_id: asked.insurance && pick.insurance !== ANOTHER ? Number(pick.insurance) : null,
		}
		const judged = {
			franchise: { asked: asked.franchise, id: body.ps_provider_id, label: body.ps_provider_id != null ? franchiseLabel(pick.franchise) : null },
			tariff:    { asked: asked.tariff, value: body.is_prepaid, label: body.is_prepaid != null ? tariffLabel(pick.tariff) : null },
			insurance: { asked: asked.insurance, id: body.ps_insurance_id, label: body.ps_insurance_id != null ? insuranceLabel(pick.insurance) : null },
		}
		setLoading(true); setError(null)
		try {
			const res = await setupApi.resolveLimits(body)
			const rulesById = new Map(rules.map(r => [String(r.id), r]))
			const v = fromEngine(res, { labels, matrixById, rulesById, judged })
			setVerdict(v); setStale(false)
			onVerdict?.(v)
		} catch (e) {
			setError({ status: e?.status ?? null, code: e?.code || null, message: e?.message || 'network error' })
		} finally {
			setLoading(false)
		}
	}
	// A verdict is about the rules as they were; when they change it stays, dimmed, with a note (UX obs. 4).
	useEffect(() => { if (verdict) setStale(true) }, [rules, matrices])

	const activityHref = `${DS_REPRICING_ROUTES.ACTIVITY_PATH}?office=${encodeURIComponent(cell.office)}&acriss=${encodeURIComponent(cell.acriss)}`
	const fieldSx = { ...inputSx, bgcolor: dsColorNew.white, '& .MuiOutlinedInput-root': { height: 40 } }
	// The engine is answering: nothing in the form can change until it does.
	const close = () => { if (!loading) onClose() }

	return (
		<AppDialog open={open} onClose={close} maxWidth="lg" paperSx={setupScopeSx}>
			<AppDialogHeader icon="magnifying-glass" title="What applies here?" onClose={close}
			                 subtitle="office · ACRISS · pickup · days → the floor of that cell and where it comes from. The rows the engine used light up in the table."/>
			<AppDialogBody sx={dialogBodySx}>
				{/* A native fieldset disables every input inside, the date picker included; pointer events off while it is. */}
				<Box component="fieldset" disabled={loading} aria-busy={loading}
				     sx={{ border: 0, p: 0, m: 0, minWidth: 0, opacity: loading ? 0.55 : 1, pointerEvents: loading ? 'none' : 'auto', transition: 'opacity 150ms' }}>
					<Stack direction="row" gap={1.75} alignItems="flex-start" flexWrap="wrap">
						<TextField select size="small" label="Office" value={cell.office} disabled={loading} sx={{ minWidth: 200, ...fieldSx }}
						           InputLabelProps={{ shrink: true }}
						           onChange={(e) => setCell(c => ({ ...c, office: e.target.value }))}>
							{!offices.length && <MenuItem value="" disabled>Loading offices…</MenuItem>}
							{offices.map(o => <MenuItem key={o.code} value={o.code}>{labels.office(o.code)}</MenuItem>)}
						</TextField>
						<Autocomplete size="small" options={allSipps} value={cell.acriss || null} disabled={loading}
						              onChange={(_, v) => setCell(c => ({ ...c, acriss: v || '' }))}
						              sx={{ width: 150 }}
						              renderInput={(p) => <TextField {...p} label="ACRISS" placeholder="CDMR" InputLabelProps={{ shrink: true }}
						                                             sx={fieldSx}
						                                             inputProps={{ ...p.inputProps, style: { fontWeight: 600 } }}/>}/>
						<DateField label="Pickup date" value={cell.pickup} onChange={(iso) => setCell(c => ({ ...c, pickup: iso }))} width={180}/>
						<TextField size="small" type="number" label="Rental days" value={cell.lor} disabled={loading} sx={{ width: 120, ...fieldSx }}
						           InputLabelProps={{ shrink: true }}
						           onChange={(e) => setCell(c => ({ ...c, lor: e.target.value }))}
						           inputProps={{ min: 1, max: 60, style: { textAlign: 'center', fontWeight: 600 } }}
						           helperText={`for a ${Number(cell.lor) >= 1 ? cell.lor : 'N'}-day rental`}/>
						{inUse.franchise.length > 0 && (
							<AxisSelect label="Franchise · of the own row" value={pick.franchise} onChange={(v) => setAxisPick(p => ({ ...p, franchise: v }))}
							            options={inUse.franchise.map(k => ({ value: k, label: franchiseLabel(k) }))} another="Another franchise"
							            disabled={loading} sx={fieldSx}
							            title="Asked because a rule is scoped to a franchise. Judged against the own row the engine takes (option A)."/>
						)}
						{inUse.tariff.length > 0 && (
							<AxisSelect label="Tariff type · of the own row" value={pick.tariff} onChange={(v) => setAxisPick(p => ({ ...p, tariff: v }))}
							            options={inUse.tariff.map(k => ({ value: k, label: tariffLabel(k) }))} another="Another tariff type"
							            disabled={loading} sx={fieldSx}/>
						)}
						{inUse.insurance.length > 0 && (
							<AxisSelect label="Insurance · of the own row" value={pick.insurance} onChange={(v) => setAxisPick(p => ({ ...p, insurance: v }))}
							            options={inUse.insurance.map(k => ({ value: k, label: insuranceLabel(k) }))} another="Another insurance"
							            disabled={loading} sx={fieldSx}/>
						)}
					</Stack>
				</Box>

				{loading ? (
					<Stack direction="row" gap={1.5} alignItems="center" justifyContent="center" role="status" aria-live="polite"
					       sx={{ mt: 2, py: 3, border: `1px dashed ${dsColorNew.grayBorder}`, borderRadius: '10px', bgcolor: dsColorNew.tableRowAlt }}>
						<CircularProgress size={22} sx={{ color: dsColorNew.appBlue }}/>
						<Box>
							<Typography sx={{ fontSize: 14, fontWeight: 600, color: dsColorNew.textTitle }}>Checking…</Typography>
							<Typography sx={{ fontSize: 12.5, color: dsColorNew.textMuted }}>
								Asking the engine what applies to {cell.office} · {cell.acriss} · {fmtDate(cell.pickup)} · {Number(cell.lor)} day{Number(cell.lor) === 1 ? '' : 's'}.
							</Typography>
						</Box>
					</Stack>
				) : (
					<>
						{!verdict && !error && (
							<Typography sx={{ fontSize: 13, color: dsColorNew.textMuted, mt: 1.5 }}>
								Pick a cell and press Check — the engine answers with the floor, the ceiling and the rules it weighed.
							</Typography>
						)}
						{error && (
							<AppNote severity="error" sx={{ mt: 1.5 }}>
								The engine did not answer ({error.code || error.status || 'network'}){error.status ? ` — webback answered ${error.status}` : ''}.{' '}
								<Link component="button" type="button" onClick={check} disabled={!canCheck}
								      sx={{ color: 'inherit', fontWeight: 600, fontSize: 'inherit', verticalAlign: 'baseline' }}>
									Try again
								</Link>
								{' '}— nothing was changed.
							</AppNote>
						)}
						{verdict && stale && !error && (
							<AppNote severity="info" sx={{ mt: 1.5 }}>The rules changed since this answer — press Check again.</AppNote>
						)}
						{verdict && (
							<Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '300px minmax(0, 1fr)' }, gap: 1.75, mt: 1.5,
							           alignItems: 'start', opacity: stale || error ? 0.55 : 1, transition: 'opacity 150ms' }}>
								<VerdictCard v={verdict} activityHref={activityHref}/>
								<Chain v={verdict}/>
							</Box>
						)}
					</>
				)}
			</AppDialogBody>
			<AppDialogFooter>
				<AppDialogCancel onClick={close} disabled={loading}>Close</AppDialogCancel>
				<AppBulkEditAction onClick={check} disabled={!canCheck} loading={loading}
				                   startIcon={<IconSeeker icon="check" sx={{ fontSize: 12 }}/>}>
					{loading ? 'Checking…' : 'Check'}
				</AppBulkEditAction>
			</AppDialogFooter>
		</AppDialog>
	)
}

function AxisSelect({ label, value, onChange, options, another, sx, title, disabled }) {
	return (
		<TextField select size="small" label={label} value={value} title={title} disabled={disabled} sx={{ minWidth: 210, ...sx }}
		           InputLabelProps={{ shrink: true }} onChange={(e) => onChange(e.target.value)}>
			{options.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
			<MenuItem value={ANOTHER}><em>{another}</em></MenuItem>
		</TextField>
	)
}
function VerdictCard({ v, activityHref }) {
	const prot = v.floorKind === 'date_protection'
	const none = v.floor == null
	const edge = none ? dsColorNew.grayBorder : prot ? dsColorNew.noteInfo : dsColorNew.appBlue
	const source = none ? 'no rule matches'
		: prot ? `from the protection “${v.floorLabel || 'unnamed'}”`
		: v.floorLabel ? `from the matrix “${v.floorLabel}”` : 'from the most specific rule'
	return (
		<Box sx={{ border: `1px solid ${dsColorNew.grayBorder}`, borderLeft: `4px solid ${edge}`, borderRadius: '10px', px: 1.75, py: 1.5, bgcolor: dsColorNew.white }}>
			<Typography sx={{ fontSize: 11, fontWeight: 600, letterSpacing: 0.6, textTransform: 'uppercase', color: dsColorNew.textMuted }}>
				Floor for this cell · {v.cell.office} · {v.cell.acriss} · {fmtDate(v.cell.pickup)} · {v.cell.lor} day{v.cell.lor === 1 ? '' : 's'}
			</Typography>
			<Typography sx={{ fontSize: 26, fontWeight: 700, lineHeight: 1.15, mt: 0.5, color: none ? dsColorNew.textSubtle : dsColorNew.textTitle }}>
				{none ? 'none' : fmtMoney(v.floor)}{' '}
				{!none && <Box component="small" sx={{ fontSize: 13, fontWeight: 500, color: dsColorNew.textMuted }}>€/day</Box>}
			</Typography>
			<Typography sx={{ fontSize: 13, fontWeight: 600, color: none ? dsColorNew.textMuted : prot ? dsColorNew.noteInfo : dsColorNew.appBlue }}>
				{source}
			</Typography>
			<Typography sx={{ fontSize: 12.5, color: dsColorNew.textMuted, mt: 0.75, lineHeight: 1.45 }}>
				{none ? 'none · the strategy is free to price it. ' : `Ceiling: ${v.ceiling == null ? 'none' : `${fmtMoney(v.ceiling)} €/day`}. `}{v.sentence}
			</Typography>
			{v.judged.map(s => (
				<Typography key={s} sx={{ fontSize: 12.5, color: dsColorNew.textMuted, mt: 0.5, lineHeight: 1.45 }}>{s}</Typography>
			))}
			<Link component={RouterLink} to={activityHref}
			      sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, mt: 1, fontSize: 13, fontWeight: 500, color: dsColorNew.appBlue,
			            textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
				See the last run on this cell in Activity <IconSeeker icon="chevron-right" sx={{ fontSize: 10 }}/>
			</Link>
		</Box>
	)
}

function Chain({ v }) {
	return (
		<Box sx={{ border: `1px solid ${dsColorNew.grayBorder}`, borderRadius: '10px', px: 1.75, py: 1.25, bgcolor: dsColorNew.white, fontSize: 13, minWidth: 0 }}>
			<Step n="1" title="Ordinary rules that match — most specific wins"/>
			{v.ordinary.length === 0 && <Line muted text="none matches this cell"/>}
			{v.ordinary.map(e => <Line key={e.id} used={e.used} struck={!e.applies || !e.used} text={e.text} note={e.note} amount={e.amount} color={dsColorNew.appBlue}/>)}
			<Step n="2" title="Protections this rental crosses — added on top, highest wins"/>
			{v.protections.length === 0 && <Line muted text="none in range of this rental"/>}
			{v.protections.map(e => <Line key={e.id} used={e.used} struck={!e.used} text={e.text} note={e.note} amount={e.amount} color={dsColorNew.noteInfo}/>)}
			<Stack direction="row" gap={1} alignItems="center" sx={{ mt: 1, pt: 1, borderTop: `1px dashed ${dsColorNew.grayBorder}` }}>
				<Mark text="="/>
				<Typography sx={{ fontWeight: 700, fontSize: 13, color: dsColorNew.textTitle }}>{v.formula}</Typography>
			</Stack>
		</Box>
	)
}
function Step({ n, title }) {
	return (
		<Stack direction="row" gap={1} alignItems="center" sx={{ mt: 0.75, mb: 0.25, '&:first-of-type': { mt: 0 } }}>
			<Mark text={n}/>
			<Typography sx={{ fontSize: 11, fontWeight: 600, letterSpacing: 0.6, textTransform: 'uppercase', color: dsColorNew.textMuted }}>{title}</Typography>
		</Stack>
	)
}
function Mark({ text }) {
	return (
		<Box sx={{ width: 18, height: 18, borderRadius: '50%', bgcolor: dsColorNew.pillTrackBg, color: dsColorNew.textMuted, fontSize: 11, fontWeight: 700,
		           display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{text}</Box>
	)
}
function Line({ used, struck, text, note, amount, color, muted }) {
	return (
		<Stack direction="row" gap={1} alignItems="baseline" sx={{ pl: 3.25, py: 0.375 }}>
			<Box component="span" sx={{ width: 14, flexShrink: 0, fontWeight: 700, color: used ? color : dsColorNew.textSubtle }}>{used ? '✓' : '·'}</Box>
			<Typography sx={{ flex: 1, fontSize: 13, minWidth: 0,
			                  color: muted ? dsColorNew.textSubtle : used ? dsColorNew.textDark : dsColorNew.textMuted,
			                  textDecoration: struck && !muted ? 'line-through' : 'none', fontStyle: muted ? 'italic' : 'normal' }}>
				{used ? <strong style={{ fontWeight: 600 }}>{text}</strong> : text}
				{note && <Box component="span" sx={{ textDecoration: 'none', display: 'inline', fontStyle: 'italic', color: dsColorNew.textSubtle }}> — {note}</Box>}
			</Typography>
			{!muted && (
				<Typography sx={{ fontWeight: 700, fontSize: 13, minWidth: 56, textAlign: 'right', fontVariantNumeric: 'tabular-nums',
				                  color: used ? color : dsColorNew.textSubtle }}>
					{amount == null ? '—' : fmtMoney(amount)}
				</Typography>
			)}
		</Stack>
	)
}
